/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useEffect, useState } from 'react'

import { Link, useNavigate } from 'react-router-dom'

import { Alert } from '@/components/ui'
import { useApi } from '@/hooks'
import { CompanyDropdown } from '@/insights/components/CompanyDropdown'
import logger from '@/utils/logger'

import { DashboardSeedPanel } from './DashboardSeedPanel'
import type { SeedSelection } from './types'

const log = logger({ category: 'CreateUserPanel' })

interface SsoDomainEntry {
  connection: string
  strategy: string
  domains?: string[]
}

const Field: React.FC<{ label: string; value: string; onChange: (v: string) => void; placeholder?: string }> = ({
  label,
  value,
  onChange,
  placeholder,
}) => (
  <label className="flex flex-col gap-1 text-sm">
    <span className="text-xs uppercase tracking-wide text-base-content/50">{label}</span>
    <input
      className="input input-bordered input-sm"
      value={value}
      placeholder={placeholder}
      aria-label={label}
      onChange={(e) => onChange(e.target.value)}
    />
  </label>
)

/**
 * Create a brand-new Insights user: a role-less Fresh account plus the
 * Insights profile, in one submit. Nobody ever handles a password — the invite
 * email carries a one-time set-password link (and if the email can't be sent,
 * the link is shown here for the admin to pass on).
 */
export const CreateUserPanel: React.FC<{ onCancel: () => void }> = ({ onCancel }) => {
  const { api } = useApi()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [userName, setUserName] = useState('')
  const [companyId, setCompanyId] = useState<number>()
  const [seed, setSeed] = useState<SeedSelection>({ mode: 'default' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [createdUid, setCreatedUid] = useState('')
  const [createdSetPasswordUrl, setCreatedSetPasswordUrl] = useState('')
  const [ssoDomains, setSsoDomains] = useState<SsoDomainEntry[]>([])

  // Which email domains belong to an SSO identity provider — best-effort; if
  // the lookup fails the wizard still creates Fresh users normally.
  useEffect(() => {
    let cancelled = false
    api
      .get<SsoDomainEntry[]>({ path: '/app/admin/sso-domains', origin: 'CreateUserPanel#ssoDomains' })
      .then((response) => {
        if (!cancelled && !response.error && response.data) setSsoDomains(response.data)
      })
      .catch((ex: unknown) => log.error(ex))
    return () => {
      cancelled = true
    }
  }, [api])

  const emailDomain = email.includes('@') ? (email.trim().toLowerCase().split('@').pop() ?? '') : ''
  const ssoConnection = emailDomain ? ssoDomains.find((entry) => entry.domains?.includes(emailDomain)) : undefined

  const canCreate =
    !saving &&
    email.trim().length >= 5 &&
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    (!!ssoConnection || userName.trim().length >= 3) &&
    companyId !== undefined &&
    (seed.mode === 'default' || (!!seed.sourceUid && (seed.dashboardSlugs?.length ?? 0) > 0))

  const create = async () => {
    if (companyId === undefined) return
    setSaving(true)
    setError('')
    try {
      const copying = seed.mode === 'copy' && seed.sourceUid && (seed.dashboardSlugs?.length ?? 0) > 0
      const seedBody = copying ? { sourceUid: seed.sourceUid, dashboardSlugs: seed.dashboardSlugs } : {}
      // SSO users get a placeholder profile re-keyed at first sign-in; everyone
      // else gets a role-less Fresh account plus a set-password invite.
      const response = ssoConnection
        ? await api.post<{ uid: string; emailSent: boolean; setPasswordUrl?: string }, object>({
            path: '/app/admin/users/pending',
            origin: 'CreateUserPanel#createPending',
            body: {
              email: email.trim(),
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              companyId,
              ...seedBody,
            },
          })
        : await api.post<{ uid: string; emailSent: boolean; setPasswordUrl?: string }, object>({
            path: '/app/admin/users/create',
            origin: 'CreateUserPanel#create',
            body: {
              email: email.trim(),
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              userName: userName.trim(),
              companyId,
              ...seedBody,
            },
          })
      if (response.error || !response.data) {
        setError(response.message || 'Could not create the user')
        return
      }
      if (response.data.emailSent) {
        navigate(`/admin/users/${encodeURIComponent(response.data.uid)}`)
      } else {
        // Fully provisioned — but the invite didn't go out, so the admin has to
        // deliver the set-password link themselves.
        setCreatedUid(response.data.uid)
        setCreatedSetPasswordUrl(response.data.setPasswordUrl ?? '')
      }
    } catch (ex) {
      log.error(ex)
      setError('Could not create the user')
    } finally {
      setSaving(false)
    }
  }

  if (createdUid) {
    return (
      <section className="flex max-w-2xl flex-col gap-3">
        <Alert
          type="warning"
          message={
            createdSetPasswordUrl
              ? 'The user was created, but the invite email could not be sent — send them this set-password link yourself.'
              : 'The user was created, but the notification email could not be sent — let them know their access is ready.'
          }
        />
        {createdSetPasswordUrl && (
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs uppercase tracking-wide text-base-content/50">Set-password link</span>
            <input
              className="input input-bordered input-sm font-mono"
              value={createdSetPasswordUrl}
              readOnly
              aria-label="Set-password link"
              onFocus={(e) => e.target.select()}
            />
          </label>
        )}
        <div>
          <Link className="link link-primary" to={`/admin/users/${encodeURIComponent(createdUid)}`}>
            View the new user
          </Link>
        </div>
      </section>
    )
  }

  return (
    <section className="flex max-w-2xl flex-col gap-5">
      <div className="rounded-lg bg-base-100 p-4 shadow">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-medium">New user</h2>
          <button type="button" className="btn btn-ghost btn-xs" onClick={onCancel}>
            Back to search
          </button>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Email" value={email} onChange={setEmail} placeholder="user@customer.com" />
          {!ssoConnection && (
            <Field label="Username" value={userName} onChange={setUserName} placeholder="unique in Fresh" />
          )}
          <Field label="First name" value={firstName} onChange={setFirstName} />
          <Field label="Last name" value={lastName} onChange={setLastName} />
          <div className="md:col-span-2">
            <CompanyDropdown value={companyId} onChange={(id) => setCompanyId(Array.isArray(id) ? id[0] : id)} />
          </div>
        </div>
        {ssoConnection && (
          <div className="mt-3">
            <Alert
              type="info"
              message={`${emailDomain} signs in through ${ssoConnection.connection} (company SSO) — no username or password is needed. Access starts at their first sign-in.`}
            />
          </div>
        )}
        <p className="mt-2 text-sm text-base-content/60">
          {ssoConnection
            ? 'The user will be emailed that their access is ready; they sign in with their company account.'
            : 'This will create a minimal user in Fresh, but the user does not need to access Fresh to use Insights. ' +
              'The invite email contains a link where the user sets their own password — no passwords change hands.'}{' '}
          This profile grants Insights access as soon as it is created.
        </p>
      </div>

      <div className="rounded-lg bg-base-100 p-4 shadow">
        <h2 className="mb-3 font-medium">Dashboards</h2>
        <DashboardSeedPanel targetCompanyId={companyId} onChange={setSeed} />
      </div>

      {error && <Alert type="error" message={error} />}

      <div className="flex items-center gap-2">
        <button type="button" className="btn btn-primary btn-sm" disabled={!canCreate} onClick={() => void create()}>
          {saving ? 'Creating…' : 'Create user'}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </section>
  )
}
